import { useEffect, useState } from "react";
import styled from "styled-components";
import { Button } from "../components/common/Button";
import { PageTitle, PageTitleDetail } from "../components/common/PageData";
import { BackButton } from "../components/common/BackButton";
import { supabaseClient } from "../shared/supabase";
import { Database } from "../database.types";

const DocumentsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 0;
  border-bottom: 1px solid #e5e5e5;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const DocumentContent = styled.div`
  flex: 1;
`;

const DocumentTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 500;
  color: #000;
`;

const DocumentDescription = styled.p`
  margin: 0;
  color: #999;
  font-size: 14px;
  line-height: 1.5;
`;

const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #bbb;
  margin-top: 8px;
`;

const DocumentFileName = styled.span`
  font-family: monospace;
  font-size: 11px;
  color: #bbb;
`;

const DocumentSize = styled.span`
  font-size: 11px;
  color: #bbb;
`;

const DownloadButton = styled(Button)`
  padding: 8px 20px;
  font-size: 13px;
  min-width: 80px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

type Document = Database["public"]["Tables"]["documents"]["Row"];

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const Documents = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const { data, error } = await supabaseClient
          .from("documents")
          .select("*")
          .order("title", { ascending: false });

        if (error) {
          console.error("Error fetching documents:", error);
          setError("Errore nel caricamento dei documenti");
          return;
        }

        setDocuments(data || []);
      } catch (err) {
        console.error("Error:", err);
        setError("Errore nel caricamento dei documenti");
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const handleOpen = (document: Document) => {
    // Open in new tab for download
    window.open(document.file_url, "_blank");
  };

  if (loading) {
    return (
      <DocumentsContainer>
        <BackButton />
        <PageTitle>DOCUMENTI</PageTitle>
        <LoadingState>Caricamento documenti...</LoadingState>
      </DocumentsContainer>
    );
  }

  if (error) {
    return (
      <DocumentsContainer>
        <BackButton />
        <PageTitle>DOCUMENTI</PageTitle>
        <EmptyState>
          <p>{error}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Riprova
          </Button>
        </EmptyState>
      </DocumentsContainer>
    );
  }

  return (
    <DocumentsContainer>
      <BackButton />

      <PageTitle>DOCUMENTI</PageTitle>
      <PageTitleDetail>
        Qui puoi visualizzare tutti i documenti disponibili
      </PageTitleDetail>

      {documents.length === 0 ? (
        <EmptyState>
          <p>Nessun documento disponibile al momento.</p>
        </EmptyState>
      ) : (
        <DocumentList>
          {documents.map((document) => (
            <DocumentItem key={document.id}>
              <DocumentContent>
                <DocumentTitle>{document.title}</DocumentTitle>
                {document.description && (
                  <DocumentDescription>
                    {document.description}
                  </DocumentDescription>
                )}
                <DocumentMeta>
                  <DocumentFileName>{document.file_name}</DocumentFileName>
                  {document.file_size && (
                    <DocumentSize>
                      • {formatFileSize(document.file_size)}
                    </DocumentSize>
                  )}
                </DocumentMeta>
              </DocumentContent>
              <DownloadButton
                variant="primary"
                onClick={() => handleOpen(document)}
              >
                Apri
              </DownloadButton>
            </DocumentItem>
          ))}
        </DocumentList>
      )}
    </DocumentsContainer>
  );
};
